import React, { useState, useEffect } from "react";
import {
  Home,
  Image,
  Movie,
  Dashboard,
  Brush,
  Star,
  Menu as MenuIcon,
  LibraryBooks,
  GridOn,
  Upgrade,
  Close,
  Rocket,
  Palette,
  Settings,
  HelpOutline,
  Code,
  LocalLibrary,
  ModelTraining,
  Texture,
  NewReleases,
  CardMembership,
  Description,
  Security,
  Groups,
  Twitter,
  GitHub,
  Discord,
  LinkedIn,
} from "@mui/icons-material";
import {
  IconButton,
  Box,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  useMediaQuery,
  Container,
  Grow,
  Slide,
  Fade,
  Paper,
  Divider,
  ListSubheader,
  CssBaseline,
  Toolbar,
} from "@mui/material";
import styles from "./ProDashboard.module.css";
import DashboardImageSlider from "./DashboardImageSlider";
import DashboardCreativity from "./DashboardCreativity";
import { useNavigate } from "react-router-dom";
import ImageIcon from '@mui/icons-material/Image';
import WebStoriesIcon from '@mui/icons-material/WebStories';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import FlipIcon from '@mui/icons-material/Flip';
import ExposureIcon from '@mui/icons-material/Exposure';
import RawOnIcon from '@mui/icons-material/RawOn';
import AddReactionIcon from '@mui/icons-material/AddReaction';
import CameraFrontIcon from '@mui/icons-material/CameraFront';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';

const ProDashboard = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate()

  useEffect(() => {
    setLoaded(true);
    if (isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const mainMenuItems = [
    { icon: <Home />, text: "Home" },
    { icon: <LibraryBooks />, text: "Library" },
    { icon: <Image />, text: "Image Creation" },
    { icon: <Movie />, text: "Motion" },
    { icon: <Brush />, text: "Realtime Canvas" },
    { icon: <GridOn />, text: "Canvas Editor" },
  ];

  const resourcesMenuItems = [
    { icon: <ModelTraining />, text: "Finetuned Models" },
    { icon: <LocalLibrary />, text: "Training & Datasets" },
    { icon: <Texture />, text: "Texture Generation" },
    { icon: <Code />, text: "Alpha Features" },
    { icon: <NewReleases />, text: "What's New" },
  ];

  const businessMenuItems = [
    { icon: <CardMembership />, text: "Premium Plans" },
    { icon: <Dashboard />, text: "API Access" },
    { icon: <Settings />, text: "Settings" },
  ];

  const legalMenuItems = [
    { icon: <HelpOutline />, text: "FAQ & Help" },
    { icon: <Description />, text: "Terms" },
    { icon: <Security />, text: "DMCA" },
    { icon: <Groups />, text: "Affiliates" },
  ];

  const socialIcons = [
    { icon: <Twitter />, link: "https://twitter.com" },
    { icon: <GitHub />, link: "https://github.com" },
    { icon: <LinkedIn />, link: "https://linkedin.com" },
  ];
  const tools = [
    { icon: <WebStoriesIcon />, link: "/dashboard/ai-background-remover",label:"Background Remover" },
    { icon: <AutoAwesomeMotionIcon />, link: "/dashboard/ai-background-changer",label:"Background Changer" },
    { icon: <FlipIcon />, link: "/dashboard/ai-image-enhancer",label:"Image Enhancer" },
    { icon: <ExposureIcon />, link: "/dashboard/ai-image-colorizer",label:"Image Colorizer" },
    { icon: <RawOnIcon />, link: "/dashboard/ai-super-resolution",label:"Super Resolution" },
    { icon: <AddReactionIcon />, link: "/dashboard/ai-face-wrap",label:"Face Wrap" },
    { icon: <CameraFrontIcon />, link: "/dashboard/ai-selfie-generator",label:"Ai Selfie Generator" },
    { icon: <SettingsOverscanIcon />, link: "/dashboard/ai-expand-images",label:"Ai Expand Images" },
  ]

  return (
    <Box className={styles.dashboardRoot}>
      <CssBaseline />
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className={styles.drawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ className: styles.drawerPaper }}
      >
        <div className={styles.sidebarHeader}>
          <div className={styles.profileSection}>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              className={styles.closeButton}
              size="large"
            >
              <Close className={styles.iconWhite} />
            </IconButton>
            <Avatar
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTdHfoXLXUXMHBP6cGrUf0R6B3xZKE7qVeOPQ&s"
              className={styles.avatar}
            />
            <div className={styles.profileText}>
              <Typography variant="subtitle1" className={styles.gradientText}>
                sjagd
              </Typography>
              <Typography variant="caption" className={styles.email}>
                user@flowstate.ai
              </Typography>
              <Button
                variant="contained"
                startIcon={<Upgrade className={styles.iconWhite} />}
                className={styles.upgradeButton}
                fullWidth
              >
                Upgrade Plan
              </Button>
            </div>
          </div>
        </div>

        <List className={styles.navList}>
          <ListSubheader className={styles.sectionHeader}>
            Workspace
          </ListSubheader>
          {mainMenuItems.map((item, index) => (
            <ListItem
              button
              className={`${styles.navItem} ${
                index === 0 ? styles.active : ""
              }`}
              key={item.text}
            >
              <ListItemIcon className={styles.listIcon}>
                {React.cloneElement(item.icon, { className: styles.iconWhite })}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                className={styles.listItemText}
              />
              <div className={styles.glowHover} />
            </ListItem>
          ))}

          <Divider className={styles.divider} />

          <ListSubheader className={styles.sectionHeader}>
            Resources
          </ListSubheader>
          {resourcesMenuItems.map((item, index) => (
            <ListItem
              button
              className={`${styles.navItem} ${
                index === 0 ? styles.active : ""
              }`}
              key={item.text}
            >
              <ListItemIcon className={styles.listIcon}>
                {React.cloneElement(item.icon, { className: styles.iconWhite })}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                className={styles.listItemText}
              />
              <div className={styles.glowHover} />
            </ListItem>
          ))}

          <Divider className={styles.divider} />

          <ListSubheader className={styles.sectionHeader}>
            Business
          </ListSubheader>
          {businessMenuItems.map((item, index) => (
            <ListItem
              button
              className={`${styles.navItem} ${
                index === 0 ? styles.active : ""
              }`}
              key={item.text}
            >
              <ListItemIcon className={styles.listIcon}>
                {React.cloneElement(item.icon, { className: styles.iconWhite })}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                className={styles.listItemText}
              />
              <div className={styles.glowHover} />
            </ListItem>
          ))}

          <Divider className={styles.divider} />

          <ListSubheader className={styles.sectionHeader}>Legal</ListSubheader>
          {legalMenuItems.map((item, index) => (
            <ListItem
              button
              className={`${styles.navItem} ${
                index === 0 ? styles.active : ""
              }`}
              key={item.text}
            >
              <ListItemIcon className={styles.listIcon}>
                {React.cloneElement(item.icon, { className: styles.iconWhite })}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                className={styles.listItemText}
              />
              <div className={styles.glowHover} />
            </ListItem>
          ))}
        </List>

        <div className={styles.socialSection}>
          <Divider className={styles.divider} />
          <div className={styles.socialIcons}>
            {socialIcons.map((social, index) => (
              <IconButton
                key={index}
                href={social.link}
                target="_blank"
                className={styles.socialIcon}
                size="large"
              >
                {React.cloneElement(social.icon, {
                  className: styles.iconWhite,
                })}
              </IconButton>
            ))}
          </div>
        </div>
      </Drawer>

      <main
        className={`${styles.mainContent} ${
          !isMobile && drawerOpen ? styles.withSidebar : ""
        }`}
      >
        {!isMobile && !drawerOpen && (
          <Toolbar>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              className={styles.sidebarToggle}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        )}

        {isMobile && (
          <Fade in={loaded} timeout={500}>
            <Toolbar className={styles.mobileHeader}>
              <IconButton
                onClick={() => setDrawerOpen(!drawerOpen)}
                className={styles.menuButton}
                size="large"
              >
                {drawerOpen ? <Close /> : <MenuIcon />}
              </IconButton>
              <Typography variant="h6" className={styles.mobileTitle}>
                Remini
              </Typography>
            </Toolbar>
          </Fade>
        )}

        <Grow in={loaded} timeout={500}>
          <Paper className={styles.hero}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>Introducing Remini</h1>
              <p className={styles.heroSubtitle}>
                Every Image is a Journey. Find Your Flow
              </p>
              <Slide in={loaded} direction="up" timeout={800}>
                <Button
                  variant="contained"
                  startIcon={<Rocket />}
                  className={styles.ctaButton}
                  size="large"
                >
                  Launch Remini
                </Button>
              </Slide>
            </div>
            <div className={styles.heroGlow} />
          </Paper>
        </Grow>

        <Container maxWidth="xl" className={styles.toolsContainer}>
          <h2 className={styles.guidesTitle}>Tools</h2>
          <div className={styles.toolsGrid}>
            {tools.map((tool, index) => (
              <Fade in={loaded} timeout={index * 200 + 500} key={index}>
                <Paper className={styles.toolCard} onClick={()=>navigate(tool.link)}>
                  <div className={styles.toolContent}>
                    {tool.icon}
                    <span>{tool.label}</span>
                  </div>
                  <div className={styles.cardGlow} />
                </Paper>
              </Fade>
            ))}
          </div>

          <h2 className={styles.guidesTitle}>Featured Guides</h2>
          <DashboardImageSlider />

          <h2 className={styles.guidesTitle}>Community Creations</h2>
          <DashboardCreativity />
        </Container>
      </main>
    </Box>
  );
};

export default ProDashboard;
